import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { formatAccessCollaborator } from '../saveCnSoftAccess'
import { Checkbox, Table } from 'antd'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'

export interface CollaboratorRef {
  coll_id: string
  name: string
}

export interface StoredCollaboratorAccess {
  coll_id: string
  access_collaborator?: boolean
  no_excel_export?: boolean
  mass_modification?: boolean
  access_export_contacts?: boolean
}

interface CollaboratorAccessRow {
  coll_id: string
  name: string
  access_collaborator: boolean
  no_excel_export: boolean
  mass_modification: boolean
  access_export_contacts: boolean
}

type AccessField = 'access_collaborator' | 'no_excel_export' | 'mass_modification' | 'access_export_contacts'

interface CnCollaboratorAccessProps {
  collaborators: CollaboratorRef[]
  initialAccess: StoredCollaboratorAccess[] | null
}

export interface CnCollaboratorAccessHandle {
  getSaveData: () => string
}

function buildInitialRows(
  collaborators: CollaboratorRef[],
  existing: StoredCollaboratorAccess[] | null,
): CollaboratorAccessRow[] {
  return [...collaborators]
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
    .map((collaborator) => {
      const existingEntry = existing?.find((entry) => entry.coll_id === collaborator.coll_id)
      return {
        coll_id: collaborator.coll_id,
        name: collaborator.name,
        access_collaborator: existingEntry?.access_collaborator !== false,
        no_excel_export:
          existingEntry?.no_excel_export === undefined || existingEntry?.no_excel_export === true,
        mass_modification: existingEntry?.mass_modification === true,
        access_export_contacts: existingEntry?.access_export_contacts === true,
      }
    })
}

function getSelectAllState(rows: CollaboratorAccessRow[], field: AccessField) {
  const checkedCount = rows.filter((row) => row[field]).length
  return {
    checked: rows.length > 0 && checkedCount === rows.length,
    indeterminate: checkedCount > 0 && checkedCount < rows.length,
  }
}

const CnCollaboratorAccess = forwardRef<CnCollaboratorAccessHandle, CnCollaboratorAccessProps>(function CnCollaboratorAccess({
  collaborators,
  initialAccess
}, ref) {
  const [rows, setRows] = useState<CollaboratorAccessRow[]>([])

  useEffect(() => {
    setRows(buildInitialRows(collaborators, initialAccess))
  }, [collaborators, initialAccess])

  useImperativeHandle(ref, () => ({
    getSaveData: () => formatAccessCollaborator(rows),
  }), [rows])

  const updateField = useCallback((collId: string, field: AccessField, value: boolean) => {
    setRows((currentRows) =>
      currentRows.map((row) => (row.coll_id === collId ? { ...row, [field]: value } : row)),
    )
  }, [])

  const updateAll = useCallback((field: AccessField, value: boolean) => {
    setRows((currentRows) => currentRows.map((row) => ({ ...row, [field]: value })))
  }, [])

  const selectAllAccess = useMemo(() => getSelectAllState(rows, 'access_collaborator'), [rows])
  const selectAllNoExcel = useMemo(() => getSelectAllState(rows, 'no_excel_export'), [rows])
  const selectAllMassModif = useMemo(() => getSelectAllState(rows, 'mass_modification'), [rows])
  const selectAllExportContacts = useMemo(
    () => getSelectAllState(rows, 'access_export_contacts'),
    [rows],
  )

  const renderHeaderCheckbox = (
    field: AccessField,
    state: { checked: boolean; indeterminate: boolean },
  ) => (
    <Checkbox
      checked={state.checked}
      indeterminate={state.indeterminate}
      onChange={(event: CheckboxChangeEvent) => updateAll(field, event.target.checked)}
    />
  )

  const renderRowCheckbox = (collId: string, field: AccessField, checked: boolean) => (
    <Checkbox checked={checked} onChange={(event) => updateField(collId, field, event.target.checked)} />
  )

  const selectAllRow: CollaboratorAccessRow & { isSelectAll?: boolean } = useMemo(
    () => ({
      coll_id: '__select_all__',
      name: 'Tout sélectionner',
      isSelectAll: true,
      access_collaborator: selectAllAccess.checked,
      no_excel_export: selectAllNoExcel.checked,
      mass_modification: selectAllMassModif.checked,
      access_export_contacts: selectAllExportContacts.checked,
    }),
    [selectAllAccess.checked, selectAllNoExcel.checked, selectAllMassModif.checked, selectAllExportContacts.checked],
  )

  const tableData = useMemo(() => (rows.length > 0 ? [selectAllRow, ...rows] : []), [rows, selectAllRow])

  const columns = [
    {
      title: 'Collaborateur',
      dataIndex: 'name',
      key: 'name',
      width: '28%',
      render: (name: string, record: CollaboratorAccessRow & { isSelectAll?: boolean }) => (
        <span style={{ fontWeight: record.isSelectAll ? 700 : 500 }}>{name}</span>
      ),
    },
    {
      title: 'Accès au logiciel ?',
      key: 'access_collaborator',
      align: 'center' as const,
      width: '18%',
      render: (_: unknown, record: CollaboratorAccessRow & { isSelectAll?: boolean }) =>
        record.isSelectAll
          ? renderHeaderCheckbox('access_collaborator', selectAllAccess)
          : renderRowCheckbox(record.coll_id, 'access_collaborator', record.access_collaborator),
    },
    {
      title: 'Supprimer export excel',
      key: 'no_excel_export',
      align: 'center' as const,
      width: '18%',
      render: (_: unknown, record: CollaboratorAccessRow & { isSelectAll?: boolean }) =>
        record.isSelectAll
          ? renderHeaderCheckbox('no_excel_export', selectAllNoExcel)
          : renderRowCheckbox(record.coll_id, 'no_excel_export', record.no_excel_export),
    },
    {
      title: 'Modif de masse',
      key: 'mass_modification',
      align: 'center' as const,
      width: '18%',
      render: (_: unknown, record: CollaboratorAccessRow & { isSelectAll?: boolean }) =>
        record.isSelectAll
          ? renderHeaderCheckbox('mass_modification', selectAllMassModif)
          : renderRowCheckbox(record.coll_id, 'mass_modification', record.mass_modification),
    },
    {
      title: 'Accès export contacts',
      key: 'access_export_contacts',
      align: 'center' as const,
      width: '18%',
      render: (_: unknown, record: CollaboratorAccessRow & { isSelectAll?: boolean }) =>
        record.isSelectAll
          ? renderHeaderCheckbox('access_export_contacts', selectAllExportContacts)
          : renderRowCheckbox(record.coll_id, 'access_export_contacts', record.access_export_contacts),
    },
  ]

  return (
    <div>
      <Table
        columns={columns}
        dataSource={tableData}
        rowKey="coll_id"
        pagination={false}
        size="small"
        bordered
        locale={{ emptyText: 'Aucun collaborateur trouvé' }}
        scroll={{ x: 900 }}
        onRow={(record: CollaboratorAccessRow & { isSelectAll?: boolean }) => ({
          style: record.isSelectAll ? { backgroundColor: '#fafafa' } : undefined,
        })}
      />
    </div>
  )
})

export default CnCollaboratorAccess
